import React, { useState } from 'react';
import { demoExamples } from '../data/demoExamples';
import { AnalysisResponse } from '../types/analysis';

interface DemoExamplesProps {
  onExampleClick: (text: string, cachedResult: AnalysisResponse, exampleId: number, severity: string, title: string) => void;
  selectedId?: number | null;
}

export const getCardIcon = (id: number, className?: string) => {
  const svgProps = {
    viewBox: '0 0 32 32',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className: className ?? 'w-9 h-9 opacity-50',
  };

  switch (id) {
    case 1: // Parasocial Trap — locked heart
      return (
        <svg {...svgProps}>
          <path d="M16 25C16 25 5 17.5 5 11C5 7.7 7.7 5 11 5C13 5 14.8 6 16 7.4C17.2 6 19 5 21 5C24.3 5 27 7.7 27 11C27 17.5 16 25 16 25Z" />
          <rect x="13.5" y="21" width="5" height="4" rx="0.8" />
          <path d="M14.2 21C14.2 19.2 17.8 19.2 17.8 21" />
        </svg>
      );
    case 2: // Subscription Trap — fishhook
      return (
        <svg {...svgProps}>
          <path d="M10 5V18C10 22 13 25 17 25C21 25 24 22 24 18C24 14 21 12 18 12" />
          <path d="M18 12L16 8" />
          <path d="M18 12L21 9" />
        </svg>
      );
    case 3: // Social Proof Pressure — crowd converging
      return (
        <svg {...svgProps}>
          <circle cx="16" cy="8" r="2.8" />
          <path d="M10.5 23C10.5 18.3 13 15.5 16 15.5C19 15.5 21.5 18.3 21.5 23" />
          <circle cx="6" cy="11" r="2.2" />
          <path d="M3 23C3 19.5 4.2 17 6 17" />
          <path d="M7.5 20L11.5 16.5" />
          <circle cx="26" cy="11" r="2.2" />
          <path d="M29 23C29 19.5 27.8 17 26 17" />
          <path d="M24.5 20L20.5 16.5" />
        </svg>
      );
    case 4: // Honest Communication — speech bubble + checkmark
      return (
        <svg {...svgProps}>
          <path d="M5 5H27C27.6 5 28 5.4 28 6V20C28 20.6 27.6 21 27 21H18L13 27V21H5C4.4 21 4 20.6 4 20V6C4 5.4 4.4 5 5 5Z" />
          <path d="M10 13.5L14 17.5L22 9.5" strokeWidth={1.8} />
        </svg>
      );
    default:
      return null;
  }
};

const DemoExamples: React.FC<DemoExamplesProps> = ({ onExampleClick }) => {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [poppingId, setPoppingId] = useState<number | null>(null);

  const getSeverityColors = (severity: string) => {
    switch (severity) {
      case 'green':
        return {
          badge: 'bg-green-500',
          border: 'border-green-300',
          hover: 'hover:border-green-500',
          iconColor: 'text-green-500',
          ring: 'ring-green-400',
        };
      case 'yellow':
        return {
          badge: 'bg-yellow-500',
          border: 'border-yellow-300',
          hover: 'hover:border-yellow-500',
          iconColor: 'text-yellow-500',
          ring: 'ring-yellow-400',
        };
      case 'orange':
        return {
          badge: 'bg-orange-500',
          border: 'border-orange-300',
          hover: 'hover:border-orange-500',
          iconColor: 'text-orange-500',
          ring: 'ring-orange-400',
        };
      case 'red':
        return {
          badge: 'bg-red-500',
          border: 'border-red-300',
          hover: 'hover:border-red-500',
          iconColor: 'text-red-500',
          ring: 'ring-red-400',
        };
      default:
        return {
          badge: 'bg-gray-500',
          border: 'border-gray-300',
          hover: 'hover:border-gray-500',
          iconColor: 'text-gray-500',
          ring: 'ring-gray-400',
        };
    }
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Try Demo Examples</h2>
        <p className="text-gray-500 text-sm">
          Click any example below to see how manipulation detection works
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {demoExamples.map((example) => {
          const colors = getSeverityColors(example.severity);
          const isHovered = hoveredId === example.id;

          const isSelected = selectedId === example.id;
          const isPopping = poppingId === example.id;

          return (
            <button
              key={example.id}
              onClick={() => {
                setSelectedId(example.id);
                setPoppingId(example.id);
                setTimeout(() => setPoppingId(null), 200);
                onExampleClick(example.conversationText, example.cachedResult, example.id, example.severity, example.title);
              }}
              onMouseEnter={() => setHoveredId(example.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`text-left p-5 bg-white border-2 ${colors.border} ${colors.hover} rounded-lg transition-all duration-200 hover:shadow-lg active:scale-95 relative ${isSelected ? `ring-2 ring-offset-1 ${colors.ring} shadow-md` : ''} ${isPopping ? 'scale-105' : ''}`}
            >
              {/* Hover tooltip - full prompt preview */}
              {isHovered && (
                <div className="absolute bottom-full left-0 right-0 mb-2 z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4 text-left">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                    Full Prompt
                  </p>
                  <p className="text-xs text-gray-700 leading-relaxed max-h-48 overflow-y-auto whitespace-pre-wrap">
                    {example.conversationText}
                  </p>
                </div>
              )}

              {/* Severity Badge + Agent Name */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors.badge}`}
                    title={`Severity: ${example.severity}`}
                  />
                  <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full leading-none">
                    {example.agentName}
                  </span>
                </div>
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  />
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {example.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-gray-600 line-clamp-3">
                {example.description}
              </p>

              {/* Click prompt */}
              <div className="mt-4 text-xs text-blue-600 font-semibold">
                Click to analyze →
              </div>

              {/* Decorative scene icon */}
              <div className={`absolute bottom-3 right-3 ${colors.iconColor}`}>
                {getCardIcon(example.id)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DemoExamples;
